import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/coreopsai.png';
import { backend_url } from '../config';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [id, setid] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    const token = searchParams.get('token');
    setToken(token);
    setid(id);
    if (!id || !token) {
      setError('Invalid or missing reset link parameter');
    }
  }, []);

  // Password strength calculator
  useEffect(() => {
    const calculateStrength = (pass) => {
      let strength = 0;
      if (pass.length >= 8) strength += 25;
      if (/[a-z]/.test(pass)) strength += 25;
      if (/[A-Z]/.test(pass)) strength += 25;
      if (/[0-9]/.test(pass)) strength += 12.5;
      if (/[^A-Za-z0-9]/.test(pass)) strength += 12.5;
      return Math.min(strength, 100);
    };
    setPasswordStrength(calculateStrength(password));
  }, [password]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${backend_url}/reset-password?id=${id}&token=${token}`,
        { password },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setMessage(response.data.message);
      setIsSubmitted(true);
      setTimeout(() => navigate('/signin'), 3000);
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setError(error.response.data.message);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Inline styles with animations and modern design
  const pageContainerStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    position: 'relative',
    overflow: 'hidden',
  };

  const backgroundShapesStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 50%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 40% 80%, rgba(120, 219, 255, 0.3) 0%, transparent 50%)
    `,
    animation: 'float 6s ease-in-out infinite',
  };

  const authContainerStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    boxShadow:
      '0 20px 40px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.05)',
    padding: '50px 40px',
    width: '100%',
    maxWidth: '450px',
    textAlign: 'center',
    position: 'relative',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transform: isSubmitted ? 'scale(1.02)' : 'scale(1)',
    transition: 'all 0.3s ease',
  };

  const logoStyle = {
    width: '140px',
    height: 'auto',
    marginBottom: '30px',
    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))',
    animation: 'bounce 2s ease-in-out infinite',
  };

  const headingStyle = {
    fontSize: '32px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '30px',
    margin: '0 0 30px 0',
  };

  const alertStyle = (type) => ({
    padding: '16px 20px',
    borderRadius: '12px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'slideIn 0.3s ease-out',
    ...(type === 'error'
      ? {
          background: 'linear-gradient(135deg, #ff6b6b, #ff5252)',
          color: '#ffffff',
          boxShadow: '0 4px 15px rgba(255, 107, 107, 0.3)',
        }
      : {
          background: 'linear-gradient(135deg, #51cf66, #40c057)',
          color: '#ffffff',
          boxShadow: '0 4px 15px rgba(81, 207, 102, 0.3)',
        }),
  });

  const formStyle = {
    textAlign: 'left',
  };

  const formGroupStyle = {
    marginBottom: '25px',
    position: 'relative',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    marginBottom: '8px',
    transform: 'translateX(0)',
    transition: 'all 0.2s ease',
  };

  const inputContainerStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const inputStyle = (isFocused) => ({
    width: '100%',
    padding: '16px 50px 16px 20px',
    fontSize: '16px',
    border: `2px solid ${isFocused ? '#667eea' : '#e2e8f0'}`,
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    transform: isFocused ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: isFocused
      ? '0 8px 25px rgba(102, 126, 234, 0.15)'
      : '0 2px 8px rgba(0, 0, 0, 0.05)',
  });

  const eyeIconStyle = {
    position: 'absolute',
    right: '16px',
    cursor: 'pointer',
    color: '#718096',
    fontSize: '18px',
    transition: 'color 0.2s ease',
    zIndex: 1,
  };

  const passwordStrengthStyle = {
    marginTop: '8px',
    height: '4px',
    backgroundColor: '#e2e8f0',
    borderRadius: '2px',
    overflow: 'hidden',
  };

  const strengthBarStyle = {
    height: '100%',
    borderRadius: '2px',
    transition: 'all 0.3s ease',
    width: `${passwordStrength}%`,
    background:
      passwordStrength < 50
        ? 'linear-gradient(90deg, #ff6b6b, #ff8e8e)'
        : passwordStrength < 75
        ? 'linear-gradient(90deg, #ffd93d, #ffb347)'
        : 'linear-gradient(90deg, #51cf66, #40c057)',
  };

  const strengthTextStyle = {
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: '500',
    color:
      passwordStrength < 50
        ? '#ff6b6b'
        : passwordStrength < 75
        ? '#ffa500'
        : '#51cf66',
  };

  const buttonStyle = {
    width: '100%',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    background: loading
      ? 'linear-gradient(135deg, #a0a0a0, #888888)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '12px',
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '20px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
    transform: loading ? 'scale(0.98)' : 'scale(1)',
  };

  const loadingSpinnerStyle = {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '10px',
  };

  const successIconStyle = {
    fontSize: '60px',
    color: '#51cf66',
    marginBottom: '20px',
    animation: 'bounce 0.6s ease-in-out',
  };

  // CSS animations as style tags
  const animationStyles = `
    @keyframes float {
      0%, 100% { transform: translateY(0px) rotate(0deg); }
      50% { transform: translateY(-20px) rotate(5deg); }
    }
    @keyframes bounce {
      0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-10px); }
      60% { transform: translateY(-5px); }
    }
    @keyframes slideIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
  `;

  const EyeIcon = ({ show, onClick }) => (
    <span onClick={onClick} style={eyeIconStyle}>
      {show ? '👁️' : '🙈'}
    </span>
  );

  const getStrengthText = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  if (isSubmitted) {
    return (
      <div style={pageContainerStyle}>
        <style>{animationStyles}</style>
        <div style={backgroundShapesStyle}></div>
        <div style={authContainerStyle}>
          <div style={successIconStyle}>✅</div>
          <h2 style={headingStyle}>Success!</h2>
          <div style={alertStyle('success')}>
            Password reset successfully! Redirecting to login...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageContainerStyle}>
      <style>{animationStyles}</style>
      <div style={backgroundShapesStyle}></div>
      <div style={authContainerStyle}>
        <img src={logo} alt='CoreOps AI' style={logoStyle} />
        <h2 style={headingStyle}>Reset Password</h2>
        {error && <div style={alertStyle('error')}>❌ {error}</div>}
        {message && <div style={alertStyle('success')}>✅ {message}</div>}
        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <label htmlFor='password' style={labelStyle}>
              🔒 New Password
            </label>
            <div style={inputContainerStyle}>
              <input
                type={showPassword ? 'text' : 'password'}
                id='password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={inputStyle(false)}
                onFocus={(e) => Object.assign(e.target.style, inputStyle(true))}
                onBlur={(e) => Object.assign(e.target.style, inputStyle(false))}
                placeholder='Enter your new password'
              />
              <EyeIcon
                show={showPassword}
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
            {password && (
              <>
                <div style={passwordStrengthStyle}>
                  <div style={strengthBarStyle}></div>
                </div>
                <div style={strengthTextStyle}>
                  Password strength: {getStrengthText()}
                </div>
              </>
            )}
          </div>
          <div style={formGroupStyle}>
            <label htmlFor='confirmPassword' style={labelStyle}>
              🔐 Confirm New Password
            </label>
            <div style={inputContainerStyle}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id='confirmPassword'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={inputStyle(false)}
                onFocus={(e) => Object.assign(e.target.style, inputStyle(true))}
                onBlur={(e) => Object.assign(e.target.style, inputStyle(false))}
                placeholder='Confirm your new password'
              />
              <EyeIcon
                show={showConfirmPassword}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            </div>
            {confirmPassword && password !== confirmPassword && (
              <div style={{ ...strengthTextStyle, color: '#ff6b6b' }}>
                ❌ Passwords do not match
              </div>
            )}
            {confirmPassword && password === confirmPassword && (
              <div style={{ ...strengthTextStyle, color: '#51cf66' }}>
                ✅ Passwords match
              </div>
            )}
          </div>
          <button
            type='submit'
            style={buttonStyle}
            disabled={loading}
            onMouseEnter={(e) =>
              !loading &&
              Object.assign(e.target.style, {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
              })
            }
            onMouseLeave={(e) =>
              !loading && Object.assign(e.target.style, buttonStyle)
            }
          >
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div style={loadingSpinnerStyle}></div>
                Resetting Password...
              </div>
            ) : (
              '🚀 Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;
